import React from "react";
import { ExternalLink, ArrowDown } from "lucide-react";
import { openVisibilityCheck } from "../../lib/visibilityCheck";

export default function VCHero() {
  const scrollToHow = () => {
    document
      .getElementById("vc-how-it-works")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="vc-hero-section"
      className="relative isolate overflow-hidden bg-navy"
    >
      <div className="absolute inset-0">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] bg-teal/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[24rem] h-[24rem] bg-teal/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-24 md:pt-28 md:pb-32 lg:pt-36 lg:pb-40">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-bold uppercase tracking-[0.18em] mb-8 animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-teal" />
            Free school leadership diagnostic · 5 minutes
          </div>

          <h1
            data-testid="vc-hero-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white leading-[1.05] animate-fade-up"
            style={{ animationDelay: "80ms" }}
          >
            Take the{" "}
            <span className="text-teal">
              Visibility Check
              <sup className="text-2xl sm:text-3xl lg:text-4xl">™</sup>
            </span>
          </h1>

          <p
            data-testid="vc-hero-subheadline"
            className="mt-8 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-2xl animate-fade-up"
            style={{ animationDelay: "160ms" }}
          >
            Discover your school's Visibility Score<sup>™</sup> and identify
            opportunities to strengthen recognition, communication, engagement,
            and school culture.
          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-up"
            style={{ animationDelay: "240ms" }}
          >
            <button
              data-testid="vc-hero-start-btn"
              onClick={openVisibilityCheck}
              title="Opens in a new tab"
              className="group inline-flex items-center justify-center gap-2 bg-teal hover:bg-[#0D6BB1] text-white font-bold px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 hover:-translate-y-0.5"
            >
              Start the Visibility Check<sup>™</sup>
              <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="vc-hero-how-btn"
              onClick={scrollToHow}
              className="group inline-flex items-center justify-center gap-2 text-white border border-white/30 hover:border-white/60 hover:bg-white/5 font-semibold px-6 py-4 rounded-lg transition-all"
            >
              How It Works
              <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
            </button>
          </div>

          <div
            className="mt-14 max-w-2xl border-l-4 border-teal pl-5 animate-fade-up"
            style={{ animationDelay: "320ms" }}
          >
            <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
              The most effective schools do more than achieve great things.
              They make those achievements visible.
            </p>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              When accomplishments, events, programs, and opportunities are
              consistently communicated and celebrated, engagement increases
              and school culture strengthens.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
